import { useEffect, useState } from "react";
import { api } from "../utils/api";

const LOOKUP_ENDPOINTS = {
  horizons: "/horizons",
  statuses: "/statuses",
  priorities: "/priorities",
  products: "/products",
  impactAreas: "/impact_areas",
};

const EMPTY_LOOKUPS = {
  horizons: [],
  statuses: [],
  priorities: [],
  products: [],
  impactAreas: [],
};

const byOrder = (a, b) => a.sortOrder - b.sortOrder;

/*
 * Every useLookups() call site shares this single cache/fetch so mounting the
 * hook in several components at once (BoardView, ListView, form fields, ...)
 * still only hits the 5 lookup endpoints once.
 */
let cache = { data: EMPTY_LOOKUPS, loading: true, error: null };
let fetchPromise = null;
let initialized = false;
const subscribers = new Set();

const notifySubscribers = () => subscribers.forEach((notify) => notify());

const fetchLookups = () => {
  if (fetchPromise) return fetchPromise;

  fetchPromise = Promise.all(
    Object.entries(LOOKUP_ENDPOINTS).map(([key, path]) =>
      api.get(path).then(({ data }) => [key, [...data].sort(byOrder)]),
    ),
  )
    .then((entries) => {
      cache = {
        data: Object.fromEntries(entries),
        loading: false,
        error: null,
      };
    })
    .catch((err) => {
      cache = { ...cache, loading: false, error: err };
    })
    .then(() => {
      initialized = true;
      notifySubscribers();
    });

  return fetchPromise;
};

// Fetches (and caches) the horizon/status/priority/product/impact-area lookup tables from the API.
export const useLookups = () => {
  const [, forceRender] = useState(0);

  useEffect(() => {
    const onUpdate = () => forceRender((n) => n + 1);
    subscribers.add(onUpdate);
    fetchLookups();
    return () => subscribers.delete(onUpdate);
  }, []);

  return { ...cache.data, loading: cache.loading, error: cache.error };
};

const invalidateCache = () => {
  fetchPromise = null;
  if (!initialized) {
    cache = { ...cache, loading: true };
    notifySubscribers();
  }
  return fetchLookups();
};

export const invalidateLookups = () => invalidateCache();

export const createLookupValue = async (type, data) => {
  const result = await api.post(LOOKUP_ENDPOINTS[type], data);
  await invalidateCache();
  return result;
};

export const updateLookupValue = async (type, id, data) => {
  const result = await api.patch(`${LOOKUP_ENDPOINTS[type]}/${id}`, data);
  await invalidateCache();
  return result;
};

export const deleteLookupValue = async (type, id) => {
  try {
    await api.delete(`${LOOKUP_ENDPOINTS[type]}/${id}`);
  } finally {
    await invalidateCache();
  }
};

export const toColorMap = (list) =>
  Object.fromEntries(list.map((item) => [item.name, item.color]));

export const useLookupColorMaps = () => {
  const { horizons, statuses, priorities, products, loading, error } =
    useLookups();

  return {
    horizonColors: toColorMap(horizons),
    statusColors: toColorMap(statuses),
    priorityColors: toColorMap(priorities),
    productColors: toColorMap(products),
    loading,
    error,
  };
};

// orderedIds must contain exactly the type's current ids, in the desired order.
export const reorderLookupValues = async (type, orderedIds) => {
  const result = await api.patch(`${LOOKUP_ENDPOINTS[type]}/reorder`, {
    orderedIds,
  });
  await invalidateCache();
  return result;
};
